/**
 * App Mode Service
 *
 * Determines and manages the current app mode based on pregnancy status and premium status.
 * Modes:
 * - 'pregnancy': User has an active pregnancy
 * - 'idle': User has no active pregnancy and is a free user
 * - 'baby': User has no active pregnancy but has premium access (baby mode after birth)
 */

import { supabase } from '@/lib/supabase';
import { AppMode, Pregnancy } from '@/store/pregnancyStore';

export interface AppModeResult {
  mode: AppMode;
  activePregnancy: Pregnancy | null;
  lastCompletedPregnancy: Pregnancy | null;
}

/**
 * Fetch app mode from database using RPC function
 */
export async function fetchAppMode(userId: string): Promise<AppMode> {
  try {
    const { data, error } = await supabase.rpc('get_app_mode', {
      p_user_id: userId,
    });

    if (error) {
      console.error('Error fetching app mode:', error);
      return 'idle';
    }

    return (data as AppMode) || 'idle';
  } catch (error) {
    console.error('Error in fetchAppMode:', error);
    return 'idle';
  }
}

/**
 * Fetch the last completed pregnancy for idle mode display
 */
export async function fetchLastCompletedPregnancy(
  userId: string
): Promise<Pregnancy | null> {
  try {
    const { data, error } = await supabase.rpc('get_last_completed_pregnancy', {
      p_user_id: userId,
    });

    if (error) {
      console.error('Error fetching last completed pregnancy:', error);
      return null;
    }

    if (!data || data.length === 0) {
      return null;
    }

    const pregnancy = data[0];
    return {
      id: pregnancy.id,
      expected_due_date: pregnancy.expected_due_date,
      child_number: 1,
      child_count: 1,
      is_high_risk: false,
      baby_names: pregnancy.baby_names || [],
      is_active: false,
      created_by: userId,
      created_at: '',
      updated_at: '',
      completion_type: pregnancy.completion_type,
      completion_date: pregnancy.completion_date,
      actual_birth_date: pregnancy.actual_birth_date,
      birth_time: pregnancy.birth_time,
      baby_data: pregnancy.baby_data,
    };
  } catch (error) {
    console.error('Error in fetchLastCompletedPregnancy:', error);
    return null;
  }
}

/**
 * Complete pregnancy with birth
 */
export async function completePregnancyBirth(
  pregnancyId: string,
  birthDate: string,
  birthTime?: string,
  babyData?: Record<string, unknown>
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('complete_pregnancy_birth', {
      p_pregnancy_id: pregnancyId,
      p_birth_date: birthDate,
      p_birth_time: birthTime || null,
      p_baby_data: babyData || null,
    });

    if (error) {
      console.error('Error completing pregnancy birth:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Error in completePregnancyBirth:', error);
    return false;
  }
}

/**
 * End pregnancy (neutral)
 */
export async function completePregnancyEnded(
  pregnancyId: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('complete_pregnancy_ended', {
      p_pregnancy_id: pregnancyId,
    });

    if (error) {
      console.error('Error ending pregnancy:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Error in completePregnancyEnded:', error);
    return false;
  }
}

// Feature access configuration by app mode
const FEATURE_ACCESS: Record<AppMode, Record<string, boolean>> = {
  pregnancy: {
    ssw_tracker: true,
    contractions: true,
    checklists: true,
    calendar: true,
    names: true,
    ai_chat: true,
    scanner: true,
    guides: true,
    dashboard: true,
    profile: true,
    wissenswertes: true,
    activity_feed: true,
  },
  idle: {
    ssw_tracker: false,
    contractions: false,
    checklists: false,
    calendar: false,
    names: false,
    ai_chat: false,
    scanner: false,
    guides: false,
    dashboard: true,
    profile: true,
    wissenswertes: false,
    activity_feed: false,
  },
  baby: {
    ssw_tracker: false,
    contractions: false,
    checklists: true,
    calendar: true,
    names: true,
    ai_chat: true,
    scanner: true,
    guides: true,
    dashboard: true,
    profile: true,
    wissenswertes: true,
    activity_feed: true,
  },
};

/**
 * Check if a feature is accessible in the current app mode
 */
export function canAccessFeature(
  feature: string,
  mode: AppMode
): boolean {
  return FEATURE_ACCESS[mode]?.[feature] ?? false;
}

/**
 * Get all features that are accessible in the current app mode
 */
export function getAccessibleFeatures(mode: AppMode): string[] {
  return Object.entries(FEATURE_ACCESS[mode] || {})
    .filter(([, accessible]) => accessible)
    .map(([feature]) => feature);
}

/**
 * Get all features that are restricted in the current app mode
 */
export function getRestrictedFeatures(mode: AppMode): string[] {
  return Object.entries(FEATURE_ACCESS[mode] || {})
    .filter(([, accessible]) => !accessible)
    .map(([feature]) => feature);
}
